import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post, request } from '../../lib/apiClient';
import { useToast } from '@vd/ui';
import styles from './Admin.module.css';
import { formatDay } from '../../lib/time';

/**
 * Admin → Provisioning (TOADO-236).
 *
 * The sales workflow: create a customer's company before they arrive,
 * stage invites without sending them, send when the deal closes, and
 * attach/detach yourself as a visible, expiring member for setup work.
 *
 * Staged invites are inert (their tokens act like they don't exist) and
 * the expiry clock starts at SEND — a workspace built two weeks early
 * neither emails anyone early nor hands out dead links later.
 */

interface ProvInvite {
  id: string;
  email: string;
  role: string;
  sentAt: string | null;
  acceptedAt: string | null;
  expiresAt: string;
}

interface ProvCompany {
  id: string;
  name: string;
  plan: string;
  seats: number;
  provisionedAt: string;
  claimedAt: string | null;
  subscriptionStatus: string;
  converted: boolean;
  provisionedByName: string | null;
  memberCount: number;
  staffAttached: boolean;
  invites: ProvInvite[];
}

interface StaffAttachment {
  userId: string;
  userName: string;
  userEmail: string;
  companyId: string;
  companyName: string;
  role: string;
  since: string;
  expiresAt: string;
}

function fmtDay(iso: string | null): string {
  if (!iso) return '-';
  return formatDay(iso);
}

/** Whole days since an ISO timestamp, floored at zero. */
function daysSince(iso: string): number {
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000));
}

export function ProvisioningPage() {
  const qc = useQueryClient();
  const toast = useToast();

  const provisioning = useQuery({
    queryKey: ['admin', 'provisioning'],
    queryFn: () => get<{ companies: ProvCompany[] }>('/admin/provisioning'),
  });
  const attachments = useQuery({
    queryKey: ['admin', 'staff-memberships'],
    queryFn: () => get<{ attachments: StaffAttachment[] }>('/admin/staff-memberships'),
  });

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['admin', 'provisioning'] });
    void qc.invalidateQueries({ queryKey: ['admin', 'staff-memberships'] });
  };

  // ---- create company form
  const [name, setName] = useState('');
  const [plan, setPlan] = useState<'team' | 'business' | 'personal' | 'free'>('team');
  const [seats, setSeats] = useState(3);
  const [ownerEmail, setOwnerEmail] = useState('');
  const createCompany = useMutation({
    mutationFn: () => post<{ id: string }>('/admin/companies', { name, plan, seats, ownerEmail }),
    onSuccess: () => {
      toast.push({ variant: 'success', title: 'Company provisioned', description: 'The owner invite is staged. Nothing has been emailed yet.' });
      setName(''); setOwnerEmail('');
      refresh();
    },
    onError: (err) => toast.push({ variant: 'danger', title: 'Provisioning failed', description: String(err) }),
  });

  const sendInvite = useMutation({
    mutationFn: (inviteId: string) => post<{ inviteUrl: string }>(`/admin/invites/${inviteId}/send`, {}),
    onSuccess: () => { toast.push({ variant: 'success', title: 'Invite sent' }); refresh(); },
    onError: (err) => toast.push({ variant: 'danger', title: 'Send failed', description: String(err) }),
  });

  const removeInvite = useMutation({
    mutationFn: (inviteId: string) => request<void>(`/admin/invites/${inviteId}`, { method: 'DELETE' }),
    onSuccess: () => { toast.push({ variant: 'success', title: 'Invite removed' }); refresh(); },
    onError: (err) => toast.push({ variant: 'danger', title: 'Remove failed', description: String(err) }),
  });

  const attach = useMutation({
    mutationFn: (companyId: string) => post(`/admin/companies/${companyId}/staff-membership`, {}),
    onSuccess: () => { toast.push({ variant: 'success', title: 'Attached for 30 days', description: 'You appear in their member list; the sweep detaches you automatically at expiry.' }); refresh(); },
    onError: (err) => toast.push({ variant: 'danger', title: 'Attach failed', description: String(err) }),
  });
  const detach = useMutation({
    mutationFn: (companyId: string) => request<void>(`/admin/companies/${companyId}/staff-membership`, { method: 'DELETE' }),
    onSuccess: () => { toast.push({ variant: 'success', title: 'Detached' }); refresh(); },
    onError: (err) => toast.push({ variant: 'danger', title: 'Detach failed', description: String(err) }),
  });

  // ---- add invites, per company
  const [inviteDrafts, setInviteDrafts] = useState<Record<string, string>>({});
  const addInvites = useMutation({
    mutationFn: ({ companyId, emails }: { companyId: string; emails: string[] }) =>
      post(`/admin/companies/${companyId}/invites`, {
        invites: emails.map((email) => ({ email, role: 'member' as const })),
      }),
    onSuccess: (_d, vars) => {
      toast.push({ variant: 'success', title: `Staged ${vars.emails.length} invite${vars.emails.length === 1 ? '' : 's'}` });
      setInviteDrafts((d) => ({ ...d, [vars.companyId]: '' }));
      refresh();
    },
    onError: (err) => toast.push({ variant: 'danger', title: 'Invite failed', description: String(err) }),
  });

  return (
    <>
      <div className={styles.pageHeader}>
        <h1 className={styles.h1}>Provisioning</h1>
      </div>
      <p className={styles.sub}>
        Set a customer's workspace up before they arrive. Invites are staged (inert, un-emailed)
        until you send them; the 7-day expiry starts at send.
      </p>

      <section className={styles.section}>
        <h2 style={{ fontSize: 'var(--fs-lg)', marginBottom: 8 }}>New customer workspace</h2>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <input className={styles.searchInput} style={{ maxWidth: 220 }} placeholder="Company name"
            value={name} onChange={(e) => setName(e.target.value)} aria-label="Company name" />
          <select className={styles.selectInput} style={{ maxWidth: 120 }} value={plan}
            onChange={(e) => setPlan(e.target.value as typeof plan)} aria-label="Plan">
            <option value="team">team</option>
            <option value="business">business</option>
            <option value="personal">personal</option>
            <option value="free">free</option>
          </select>
          {(plan === 'team' || plan === 'business') && (
            <input className={styles.searchInput} style={{ maxWidth: 90 }} type="number" min={plan === 'business' ? 3 : 1} max={100}
              value={seats} onChange={(e) => setSeats(Number(e.target.value))} aria-label="Seats" />
          )}
          <input className={styles.searchInput} style={{ maxWidth: 240 }} placeholder="Owner email"
            value={ownerEmail} onChange={(e) => setOwnerEmail(e.target.value)} aria-label="Owner email" />
          <button type="button" className={styles.expandBtn}
            disabled={createCompany.isPending || !name.trim() || !ownerEmail.trim()}
            onClick={() => createCompany.mutate()}>
            {createCompany.isPending ? 'Creating…' : 'Create + stage owner invite'}
          </button>
        </div>
      </section>

      <section className={styles.section}>
        <h2 style={{ fontSize: 'var(--fs-lg)', marginBottom: 8 }}>Provisioned workspaces</h2>
        {provisioning.isLoading && <p className={styles.muted}>Loading…</p>}
        {provisioning.data?.companies.length === 0 && <p className={styles.muted}>None yet.</p>}
        {provisioning.data?.companies.map((c) => (
          <div key={c.id} style={{ borderTop: '1px solid var(--border)', padding: '10px 0' }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <strong>{c.name}</strong>
              <span className={styles.planBadge}>{c.plan}</span>
              {c.claimedAt
                ? <span className={styles.muted}>claimed {fmtDay(c.claimedAt)}</span>
                : <span className={styles.overrideBadge} title="Created by staff; the customer has not accepted the owner invite yet. Excluded from signup funnels until claimed.">unclaimed</span>}
              {/* Three claimed states (review round 1): a churned
                  workspace also has no Stripe sub id (the deleted-
                  webhook nulls it, status 'canceled'), so it must not
                  read as "never added billing"; enterprise is invoiced
                  by hand and never Stripe-managed. */}
              {c.claimedAt && !c.converted && c.subscriptionStatus === 'canceled' && (
                <span className={styles.muted} title="Had a paid subscription and canceled. Counted in churn, not in the unconverted pipeline.">churned</span>
              )}
              {c.claimedAt && !c.converted && c.subscriptionStatus !== 'canceled' && c.plan === 'enterprise' && (
                <span className={styles.muted} title="Enterprise is invoiced by hand and never Stripe-managed; no subscription is expected.">invoiced</span>
              )}
              {c.claimedAt && !c.converted && c.subscriptionStatus !== 'canceled' && c.plan !== 'enterprise' && (
                <span
                  className={styles.overrideBadge}
                  title="Claimed, but the customer never added billing. Claiming grants ownership without creating a subscription, so nothing forces this step; the workspace is using its plan quota unbilled (TOADO-259)."
                >
                  unconverted {daysSince(c.claimedAt)}d
                </span>
              )}
              {c.claimedAt && c.converted && (
                <span className={styles.muted} title={'Stripe subscription exists (status: ' + c.subscriptionStatus + ').'}>converted</span>
              )}
              <span className={styles.muted}>
                {c.memberCount} member{c.memberCount === 1 ? '' : 's'} · by {c.provisionedByName ?? 'staff'} · {fmtDay(c.provisionedAt)}
              </span>
              <span style={{ flex: 1 }} />
              {c.staffAttached
                ? <button type="button" className={styles.expandBtn} onClick={() => detach.mutate(c.id)}>Detach me</button>
                : <button type="button" className={styles.expandBtn} onClick={() => attach.mutate(c.id)}>Attach me (30d)</button>}
            </div>
            <div style={{ marginTop: 6 }}>
              {c.invites.map((i) => (
                <div key={i.id} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 'var(--fs-sm)', padding: '2px 0' }}>
                  <span>{i.email}</span>
                  <span className={styles.muted}>{i.role}</span>
                  {i.acceptedAt
                    ? <span className={styles.muted}>accepted {fmtDay(i.acceptedAt)}</span>
                    : i.sentAt
                      ? <span className={styles.muted}>sent {fmtDay(i.sentAt)} · expires {fmtDay(i.expiresAt)}</span>
                      : <span className={styles.overrideBadge} title="Created but never sent. Inert: its link does not work until you send it, and the 7-day expiry starts then.">staged</span>}
                  {!i.acceptedAt && (
                    <>
                      <button type="button" className={styles.linkChip} disabled={sendInvite.isPending}
                        onClick={() => sendInvite.mutate(i.id)}>
                        {i.sentAt ? 'Resend' : 'Send'}
                      </button>
                      <button type="button" className={styles.linkChip} disabled={removeInvite.isPending}
                        style={{ color: 'var(--danger)' }}
                        title={i.sentAt ? 'Remove this invite (its emailed link stops working)' : 'Remove this staged invite (nothing was ever sent)'}
                        onClick={() => removeInvite.mutate(i.id)}>
                        Remove
                      </button>
                    </>
                  )}
                </div>
              ))}
              <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                <input
                  className={styles.searchInput}
                  style={{ maxWidth: 340 }}
                  placeholder="Stage member invites (comma-separated emails)"
                  value={inviteDrafts[c.id] ?? ''}
                  onChange={(e) => setInviteDrafts((d) => ({ ...d, [c.id]: e.target.value }))}
                  aria-label={`Stage invites for ${c.name}`}
                />
                <button type="button" className={styles.expandBtn}
                  disabled={addInvites.isPending || !(inviteDrafts[c.id] ?? '').trim()}
                  onClick={() => addInvites.mutate({
                    companyId: c.id,
                    emails: (inviteDrafts[c.id] ?? '').split(',').map((s) => s.trim()).filter(Boolean),
                  })}>
                  Stage
                </button>
              </div>
            </div>
          </div>
        ))}
      </section>

      <section className={styles.section}>
        <h2 style={{ fontSize: 'var(--fs-lg)', marginBottom: 8 }}>Active staff attachments</h2>
        <p className={styles.sub} style={{ marginBottom: 8 }}>
          Every staff membership across all workspaces, on one screen. This is the answer to
          "who has had access to our data", and it must never need assembling from audit logs.
        </p>
        {attachments.data?.attachments.length === 0 && <p className={styles.muted}>None.</p>}
        {attachments.data?.attachments.map((a) => (
          <div key={`${a.userId}:${a.companyId}`} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '4px 0', fontSize: 'var(--fs-sm)' }}>
            <strong>{a.userName}</strong>
            <span className={styles.muted}>{a.userEmail}</span>
            <span>→ {a.companyName}</span>
            <span className={styles.muted}>since {fmtDay(a.since)} · expires {fmtDay(a.expiresAt)}</span>
            <button type="button" className={styles.linkChip} onClick={() => detach.mutate(a.companyId)}>Detach</button>
          </div>
        ))}
      </section>
    </>
  );
}
